import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Trash2, Eye, EyeOff, RefreshCw, LoaderCircle } from 'lucide-react';
import { Section } from '../settingsPrimitives';
import { useTerminalConfigStore } from '../../store/terminalConfigStore';
import { useNotificationStore } from '../../store/notificationStore';
import TermIconButton from './TermIconButton';
import { PaneIcon } from './TerminalPaneHeader';
import ProfileEditorModal from './ProfileEditorModal';
import './TerminalMenu.css';
import './TerminalProfiles.css';

/**
 * Settings → Extensions → Terminal → Profiles: the agents the "+" menu
 * offers, whether each is installed (and where), hide/show a built-in, edit,
 * add and delete your own.
 */
export default function TerminalProfilesSettings() {
  const { t } = useTranslation();
  const profiles = useTerminalConfigStore((s) => s.profiles);
  const detection = useTerminalConfigStore((s) => s.detection);
  const detecting = useTerminalConfigStore((s) => s.detecting);
  const [editing, setEditing] = useState(null); // profile | {} (new) | null

  useEffect(() => {
    const st = useTerminalConfigStore.getState();
    st.load().then(() => st.detect());
  }, []);

  const remove = (p) => {
    useTerminalConfigStore.getState().deleteProfile(p.id);
    useNotificationStore.getState().addNotification({
      type: 'success',
      title: t('terminalExt.profileDeleted', 'Profile deleted'),
      message: p.name,
      duration: 3000,
    });
  };

  const status = (p) => {
    if (!(p.command in detection)) return <span className="term-prof-status">{t('terminalExt.checking', 'Checking…')}</span>;
    const d = detection[p.command];
    return d ? (
      <span className="term-prof-status term-prof-status--ok">{t('terminalExt.installedAt', 'Installed · {{path}}', { path: d.path })}</span>
    ) : (
      <span className="term-prof-status term-prof-status--bad">{t('terminalExt.notInstalled', 'Not installed')}</span>
    );
  };

  return (
    <Section title={t('terminalExt.profilesTitle', 'Profiles')}>
      <div className="term-prof-intro">
        {t('terminalExt.profilesIntro', 'Programs you can open in a terminal pane with one click, from the + menu.')}
      </div>
      <div className="term-prof-list" role="list">
        {profiles.map((p) => (
          <div key={p.id} className={`term-prof-row${p.hidden ? ' term-prof-row--hidden' : ''}`} role="listitem">
            <PaneIcon pane={{ icon: p.icon, profileId: p.builtin ? p.id : undefined }} />
            <div className="term-prof-main">
              <div className="term-prof-name">
                {p.name}
                <code className="term-prof-cmd">{[p.command, ...p.args].join(' ')}</code>
              </div>
              {status(p)}
            </div>
            <div className="term-prof-actions">
              {p.builtin && (
                <TermIconButton
                  className="term-menu-mini"
                  label={p.hidden ? t('terminalExt.showInMenu', 'Show in the + menu') : t('terminalExt.hideFromMenu', 'Hide from the + menu')}
                  onClick={() => useTerminalConfigStore.getState().setProfileHidden(p.id, !p.hidden)}
                >
                  {p.hidden ? <EyeOff size={13} /> : <Eye size={13} />}
                </TermIconButton>
              )}
              <TermIconButton className="term-menu-mini" label={t('terminalExt.editProfileShort', 'Edit')} onClick={() => setEditing(p)}>
                <Pencil size={13} />
              </TermIconButton>
              {!p.builtin && (
                <TermIconButton className="term-menu-mini" label={t('terminalExt.deleteProfile', 'Delete profile')} onClick={() => remove(p)}>
                  <Trash2 size={13} />
                </TermIconButton>
              )}
            </div>
          </div>
        ))}
      </div>
      <div className="term-prof-foot">
        <button type="button" className="settings-action-btn" onClick={() => setEditing({})}>
          <Plus size={13} aria-hidden />
          {t('terminalExt.addProfile', 'Add profile')}
        </button>
        <button
          type="button"
          className="term-menu-foot-btn"
          onClick={() => useTerminalConfigStore.getState().detect(true)}
          disabled={detecting}
        >
          {detecting ? <LoaderCircle size={13} className="animate-spin" aria-hidden /> : <RefreshCw size={13} aria-hidden />}
          {t('terminalExt.recheck', 'Re-check')}
        </button>
      </div>
      {editing && <ProfileEditorModal profile={editing.id ? editing : null} onClose={() => setEditing(null)} />}
    </Section>
  );
}
